import { useContext } from 'react';
import { ThemeContext } from '@/theme/ThemeProvider';
import { useTranslation } from 'react-i18next';
import BigNumber from 'bignumber.js';
import type { ReferralTier, ReferralFriend } from '@/app/hooks/trade/tradeMission';
import { formatDecimal } from '@/utils/formatDecimal';
import * as S from './styles';

interface ReferralTableProps {
  friends: ReferralFriend[];
  tiers: ReferralTier[];
  onClaimReward: (missionId: number, tierIndex: number, rewardAmount: string | number, referralUserId: number) => void;
  missionId: number;
}

export const ReferralTable: React.FC<ReferralTableProps> = ({
  friends,
  tiers,
  onClaimReward,
  missionId,
}) => {
  const { t } = useTranslation();
  const { isDarkMode } = useContext(ThemeContext);

  if (friends.length === 0) {
    return (
      <S.ReferralEmptyState isDarkmode={isDarkMode}>
        <S.ReferralEmptyIcon>👥</S.ReferralEmptyIcon>
        <S.ReferralEmptyTitle>
          {t('No friends invited yet', { defaultValue: '아직 초대한 친구가 없습니다' })}
        </S.ReferralEmptyTitle>
        <S.ReferralEmptyDesc isDarkmode={isDarkMode}>
          {t('Invite friends to earn rewards', { defaultValue: '친구를 초대하고 보상을 받아보세요!' })}
        </S.ReferralEmptyDesc>
      </S.ReferralEmptyState>
    );
  }

  return (
    <>
      <S.ReferralTableTitle>
        {t('Friend Progress Details', { defaultValue: '친구별 진행 상황' })} ({friends.length}{' '}
        {t('people', { defaultValue: '명' })})
      </S.ReferralTableTitle>

      {/* 테이블 */}
      <S.ReferralTableWrapper isDarkmode={isDarkMode}>
        <S.ReferralTable>
          {/* 헤더 */}
          <S.ReferralTableHeader isDarkmode={isDarkMode}>
            <tr>
              <S.ReferralTableHeaderCell className="center">
                {t('userId', { defaultValue: '사용자 ID' })}
              </S.ReferralTableHeaderCell>
              <S.ReferralTableHeaderCell className="right">
                {t('totalDeposit', { defaultValue: '총 입금액' })} (USDT)
              </S.ReferralTableHeaderCell>
              <S.ReferralTableHeaderCell className="right">
                {t('totalVolume', { defaultValue: '총 거래량' })} (USDT)
              </S.ReferralTableHeaderCell>
              {tiers.map((tier, tierIndex) => (
                <S.ReferralTableHeaderCell key={tierIndex} className="center">
                  <S.TierHeaderLabel>
                    {t('tier', { defaultValue: '티어' })} {tierIndex + 1}
                  </S.TierHeaderLabel>
                  <S.TierHeaderReward>
                    ({new BigNumber(tier.rewardAmount).toFormat()} USDT)
                  </S.TierHeaderReward>
                </S.ReferralTableHeaderCell>
              ))}
            </tr>
          </S.ReferralTableHeader>

          {/* 바디 */}
          <S.ReferralTableBody>
            {friends.map((friend, friendIndex) => (
              <S.ReferralTableRow key={friend.userId} isDarkmode={isDarkMode} isEven={friendIndex % 2 === 0}>
                {/* User ID */}
                <S.ReferralTableCell className="center">{friend.userId}</S.ReferralTableCell>

                {/* 총 입금액 */}
                <S.ReferralTableCell className="right">
                  {new BigNumber(friend.totalDeposit).toFormat()}
                </S.ReferralTableCell>

                {/* 총 거래량 (소수점 제거, 내림) */}
                <S.ReferralTableCell className="right">
                  {new BigNumber(formatDecimal(friend.totalVolume, 0)).toFormat()}
                </S.ReferralTableCell>

                {/* 각 티어별 상태 */}
                {tiers.map((tier, tierIndex) => {
                  const isAchieved = friend.achievedTiers.includes(tierIndex);
                  const isClaimed = friend.claimedTiers.includes(tierIndex);
                  const canClaim = isAchieved && !isClaimed;

                  return (
                    <S.ReferralTableCell key={tierIndex} className="center">
                      {isClaimed ? (
                        <S.TierStatus>
                          <span>✅</span>
                          <span>{t('Rewarded')}</span>
                        </S.TierStatus>
                      ) : canClaim ? (
                        <S.TierClaimButton
                          onClick={() => onClaimReward(missionId, tierIndex, tier.rewardAmount, friend.userId)}
                        >
                          <span>🎁</span>
                          <span>{t('Claim Reward')}</span>
                        </S.TierClaimButton>
                      ) : (
                        <S.TierNotAchieved>
                          <span>❌</span>
                          <span>{t('Not Achieved')}</span>
                        </S.TierNotAchieved>
                      )}
                    </S.ReferralTableCell>
                  );
                })}
              </S.ReferralTableRow>
            ))}
          </S.ReferralTableBody>
        </S.ReferralTable>
      </S.ReferralTableWrapper>

      {/* 티어 조건 설명 */}
      <S.TierConditionsBox isDarkmode={isDarkMode}>
        <S.TierConditionsTitle isDarkmode={isDarkMode}>
          {t('tierConditions', { defaultValue: '티어 조건' })}
        </S.TierConditionsTitle>
        <S.TierConditionsList>
          {tiers.map((tier, tierIndex) => {
            const minDeposit = new BigNumber(tier.minDeposit || 0);
            const minVolume = new BigNumber(formatDecimal(tier.minVolume || 0, 0));
            return (
              <S.TierConditionItem key={tierIndex} isDarkmode={isDarkMode}>
                <S.TierConditionLabel>
                  {t('tier', { defaultValue: '티어' })} {tierIndex + 1}:
                </S.TierConditionLabel>
                <span>
                  {t('deposit', { defaultValue: '입금' })} {minDeposit.toFormat()} USDT +
                </span>
                <span>
                  {t('Volume', { defaultValue: '거래량' })} {minVolume.toFormat()} USDT
                </span>
                <S.TierConditionReward>
                  → {new BigNumber(tier.rewardAmount).toFormat()} USDT
                </S.TierConditionReward>
              </S.TierConditionItem>
            );
          })}
        </S.TierConditionsList>
      </S.TierConditionsBox>
    </>
  );
};
